
import React, { useRef, useEffect } from 'react';

interface AudioWaveformProps {
  isRecording: boolean;
  audioContext: AudioContext | null;
  analyser: AnalyserNode | null;
}

const AudioWaveform: React.FC<AudioWaveformProps> = ({ 
  isRecording, 
  audioContext, 
  analyser 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  // Use a ref to track the current recording state to avoid stale closures
  const isRecordingRef = useRef(isRecording);

  // Update the ref whenever isRecording changes
  useEffect(() => {
    // Update the ref to the current value
    isRecordingRef.current = isRecording;
    
    // If recording just changed status, log it for debugging
    console.log(`AudioWaveform: isRecording changed to ${isRecording}`);
    
    // If we're not recording, ensure any animation is stopped
    if (!isRecording && animationFrameRef.current) {
      console.log("Canceling animation frame due to isRecording change to false");
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
      
      // Draw the flat line when stopping
      drawFlatLine();
    }
  }, [isRecording]);

  // Initialize dataArray when analyser becomes available
  useEffect(() => {
    if (analyser && !dataArrayRef.current) {
      console.log("Creating data array from analyser");
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
    }
  }, [analyser]);

  // Handle animation logic
  useEffect(() => {
    // Clean up function to cancel any animation frame
    const cleanup = () => {
      if (animationFrameRef.current) {
        console.log("Cleanup: canceling animation frame");
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };

    // Resume AudioContext if needed
    if (audioContext && audioContext.state === 'suspended') {
      console.log("Resuming audio context");
      audioContext.resume().catch(console.error);
    }

    // If we should be animating (recording with analyser available)
    if (isRecording && analyser && dataArrayRef.current) {
      console.log("Starting waveform animation");
      cleanup(); // Ensure no duplicate animations
      drawWaveform(); // Start animation
    } 
    // Stop animation and draw flat line when not recording
    else if (!isRecording) {
      console.log("Drawing flat line (not recording)");
      cleanup(); // Stop any animation
      drawFlatLine(); // Draw flat line
    }
    
    // Clean up on unmount or deps change
    return cleanup;
  }, [isRecording, analyser, audioContext]);

  // Draw a flat line when not recording
  const drawFlatLine = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Reset canvas with proper dimensions
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    
    ctx.scale(dpr, dpr);
    
    // Clear canvas
    ctx.fillStyle = '#f1f0fb';
    ctx.fillRect(0, 0, rect.width, rect.height);
    
    // Draw flat line
    ctx.beginPath();
    ctx.moveTo(0, rect.height / 2);
    ctx.lineTo(rect.width, rect.height / 2);
    ctx.strokeStyle = '#9b87f5';
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  // Animation function for drawing the waveform
  const drawWaveform = () => {
    // Get references to needed objects
    if (!analyser || !dataArrayRef.current || !canvasRef.current) {
      console.log("Missing required objects for waveform drawing");
      return;
    }
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas dimensions for high-DPI displays
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    
    ctx.scale(dpr, dpr);
    
    // The actual drawing function called on each frame
    const draw = () => {
      // Check if we should still be drawing
      if (!isRecordingRef.current || !analyser || !dataArrayRef.current) {
        console.log("Recording stopped or resources missing, stopping animation");
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        return;
      }
      
      // Get time-domain data from analyser
      analyser.getByteTimeDomainData(dataArrayRef.current);
      
      // Clear background
      ctx.fillStyle = '#f1f0fb';
      ctx.fillRect(0, 0, rect.width, rect.height);
      
      // Set line style
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#9b87f5';
      ctx.beginPath();
      
      // Calculate width of each data point
      const sliceWidth = rect.width / dataArrayRef.current.length;
      let x = 0;
      
      // Draw the waveform line
      for (let i = 0; i < dataArrayRef.current.length; i++) {
        const v = dataArrayRef.current[i] / 128.0; // Normalize to range around 1
        const y = v * rect.height / 2;
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
        
        x += sliceWidth;
      }
      
      ctx.lineTo(rect.width, rect.height / 2);
      ctx.stroke();
      
      // Add gradient underlay for visual appeal
      const gradient = ctx.createLinearGradient(0, 0, 0, rect.height);
      gradient.addColorStop(0, 'rgba(155, 135, 245, 0.0)');
      gradient.addColorStop(0.5, 'rgba(155, 135, 245, 0.05)');
      gradient.addColorStop(1, 'rgba(155, 135, 245, 0.0)');
      
      ctx.fillStyle = gradient;
      ctx.lineTo(rect.width, rect.height);
      ctx.lineTo(0, rect.height);
      ctx.fill();
      
      // Continue animation only if we're still recording
      if (isRecordingRef.current) {
        animationFrameRef.current = requestAnimationFrame(draw);
      } else {
        console.log("Recording stopped, not requesting next animation frame");
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      }
    };
    
    // Start the animation loop
    draw();
  };

  return (
    <div className="w-full mt-2 rounded-md overflow-hidden">
      <canvas 
        ref={canvasRef} 
        className="w-full h-20 bg-soft-purple rounded-md"
      />
    </div>
  );
};

export default AudioWaveform;
